import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaStar,
  FaGift,
  FaShieldAlt,
  FaClock,
  FaChartLine,
  FaUsers,
  FaTrophy,
  FaArrowRight,
  FaCheck,
  FaMobileAlt,
  FaCreditCard,
  FaGamepad,
  FaCrown,
  FaLock,
  FaNewspaper,
  FaCoins,
  FaHandHoldingHeart,
  FaEnvelope,
  FaPlay,
  FaDesktop,
  FaCertificate,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import { WebsiteJSONLD, OrganizationJSONLD } from '../utils/seo';
import NewsModal from '../components/NewsModal';

const Home: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const [currentImage, setCurrentImage] = useState(0);
  const [selectedNews, setSelectedNews] = useState<{
    title: string;
    date: string;
    excerpt: string;
    fullText: string;
  } | null>(null);
  const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);

  useEffect(() => {
    // Preload second image after initial load
    const preloadImage = () => {
      const img = new Image();
      img.src = '/images/heromainbg2.png';
    };
    
    // Preload after 2 seconds to not block initial render
    const preloadTimeout = setTimeout(preloadImage, 2000);
    
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev === 0 ? 1 : 0));
    }, 5000); // Switch every 5 seconds

    return () => {
      clearInterval(interval);
      clearTimeout(preloadTimeout);
    };
  }, []);

  const openNewsModal = (article: {
    title: string;
    date: string;
    excerpt: string;
    fullText: string;
  }) => {
    setSelectedNews(article);
    setIsNewsModalOpen(true);
  };

  const closeNewsModal = () => {
    setIsNewsModalOpen(false);
    setSelectedNews(null);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const heroAnchors = [
    { id: 'top-offers', label: 'Top Offers', icon: FaTrophy },
    { id: 'comparison', label: 'Compare', icon: FaChartLine },
    { id: 'bonuses', label: 'Bonuses', icon: FaGift },
    { id: 'reviews', label: 'Reviews', icon: FaStar },
    { id: 'features', label: 'Features', icon: FaShieldAlt },
    { id: 'testimonials', label: 'Testimonials', icon: FaUsers },
  ];

  const topOffers = [
    {
      id: 'casinocasino',
      name: 'CasinoCasino',
      bonus: '100% up to £500 + 50 Spins',
      rating: 4.8,
      image: '/images/offers/casinocasino.webp',
      link: '/offers/casinocasino',
      url: 'https://www.casinocasino.com',
    },
    {
      id: 'mrplay',
      name: 'MrPlay',
      bonus: '200% up to £200 + 100 Spins',
      rating: 4.7,
      image: '/images/offers/mrplay.webp',
      link: '/offers/mrplay',
      url: 'https://www.mrplay.com/',
    },
    {
      id: 'partycasino',
      name: 'PartyCasino',
      bonus: '150% up to £300 + 75 Spins',
      rating: 4.9,
      image: '/images/offers/partycasino.webp',
      link: '/offers/partycasino',
      url: 'https://www.partycasino.com/',
    },
  ];

  const features = [
    {
      icon: FaShieldAlt,
      title: 'Licensed & Secure',
      description: 'All casinos are UKGC licensed and fully regulated for your safety',
    },
    {
      icon: FaGift,
      title: 'Exclusive Bonuses',
      description: 'Access special offers and promotions not available elsewhere',
    },
    {
      icon: FaClock,
      title: 'Fast Payouts',
      description: 'Quick withdrawal times with verified payment methods',
    },
    {
      icon: FaChartLine,
      title: 'Detailed Reviews',
      description: 'In-depth analysis of games, bonuses, and user experience',
    },
  ];

  const stats = [
    { value: '500+', label: 'Casinos Reviewed' },
    { value: '50K+', label: 'Happy Players' },
    { value: '98%', label: 'Satisfaction Rate' },
    { value: '24/7', label: 'Support Available' },
  ];

  return (
    <>
      <SEO
        title="UK Casino Offers & Reviews"
        description="Discover the best UK casino offers with comprehensive reviews, exclusive bonuses, and detailed comparisons. Find your perfect casino match today."
        url="/"
      />
      <WebsiteJSONLD />
      <OrganizationJSONLD />

      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
      >
        {/* First Background Image */}
        <div
          className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
            currentImage === 0 ? 'opacity-30' : 'opacity-0'
          }`}
          style={{
            backgroundImage: 'url(/images/heromainbg.webp)',
          }}
        />
        {/* Second Background Image - Load only when needed */}
        <div
          className={`absolute inset-0 bg-cover bg-right bg-no-repeat transition-opacity duration-1000 ${
            currentImage === 1 ? 'opacity-30' : 'opacity-0'
          }`}
          style={{
            backgroundImage: currentImage === 1 ? 'url(/images/heromainbg2.png)' : 'none',
          }}
        />

        <div className="relative z-10 container mx-auto px-4 py-20 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
            <span className="text-gradient">Premium UK Casino</span>
            <br />
            <span className="text-dark-text">Offers & Reviews</span>
          </h1>
          <p className="text-xl md:text-2xl text-dark-muted mb-12 max-w-3xl mx-auto animate-slide-up">
            Unlock exclusive bonuses, compare top-rated casinos, and make informed decisions with our comprehensive reviews
          </p>

          {/* Hero Anchor Links */}
          <div className="flex flex-wrap justify-center gap-4 mb-12 animate-slide-up">
            {heroAnchors.map((anchor) => (
              <button
                key={anchor.id}
                onClick={() => scrollToSection(anchor.id)}
                className="group px-6 py-3 glass-effect rounded-lg hover:bg-accent-primary/20 transition-all flex items-center gap-2"
              >
                <anchor.icon className="text-accent-primary group-hover:scale-110 transition-transform" />
                <span className="font-semibold">{anchor.label}</span>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-4 animate-slide-up">
            <Link
              to="/comparison"
              className="px-8 py-4 bg-gradient-to-r from-accent-primary to-accent-secondary hover:opacity-90 rounded-lg font-semibold text-lg transition-opacity flex items-center gap-2"
            >
              Compare Offers
              <FaArrowRight />
            </Link>
            <button
              onClick={() => scrollToSection('top-offers')}
              className="px-8 py-4 glass-effect hover:bg-accent-primary/20 rounded-lg font-semibold text-lg transition-all"
            >
              View Top Offers
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="text-4xl md:text-5xl font-bold text-gradient mb-2">
                  {stat.value}
                </div>
                <div className="text-dark-muted">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Offers Section */}
      <section id="top-offers" className="py-20 container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gradient">Top Casino Offers</span>
          </h2>
          <p className="text-xl text-dark-muted max-w-2xl mx-auto">
            Handpicked selections featuring the most generous bonuses and best player experiences
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {topOffers.map((offer, index) => (
            <div
              key={offer.id}
              className="glass-effect rounded-2xl p-6 hover:scale-105 transition-transform animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="relative mb-4">
                <img
                  src={offer.image}
                  alt={offer.name}
                  className="w-full h-48 object-contain rounded-lg"
                  width="400"
                  height="192"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute top-4 right-4 bg-accent-gold px-3 py-1 rounded-full flex items-center gap-1">
                  <FaStar className="text-yellow-400" />
                  <span className="font-semibold">{offer.rating}</span>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-2">{offer.name}</h3>
              <p className="text-accent-primary font-semibold mb-4">{offer.bonus}</p>
              <div className="flex flex-col gap-3">
                <a
                  href={offer.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary hover:opacity-90 rounded-lg font-semibold text-center transition-opacity flex items-center justify-center gap-2"
                >
                  Claim Bonus Now
                  <FaArrowRight />
                </a>
                <Link
                  to={offer.link}
                  className="inline-flex items-center justify-center gap-2 text-accent-primary hover:text-accent-secondary transition-colors font-semibold text-sm"
                >
                  Read Full Review
                  <FaArrowRight />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Choose <span className="text-gradient">GameObsidian</span>
            </h2>
            <p className="text-xl text-dark-muted max-w-2xl mx-auto">
              We provide everything you need to find the perfect casino match
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="glass-effect rounded-xl p-6 text-center hover:scale-105 transition-transform animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="bg-accent-primary/20 p-4 rounded-full inline-flex mb-4">
                  <feature.icon className="text-3xl text-accent-primary" />
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-dark-muted">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison CTA */}
      <section id="comparison" className="py-20 container mx-auto px-4">
        <div className="glass-effect rounded-2xl p-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Compare All <span className="text-gradient">UK Casino Offers</span>
          </h2>
          <p className="text-xl text-dark-muted mb-8 max-w-2xl mx-auto">
            Side-by-side comparison of bonuses, features, and ratings to find your ideal casino
          </p>
          <Link
            to="/comparison"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-accent-primary to-accent-secondary hover:opacity-90 rounded-lg font-semibold text-lg transition-opacity"
          >
            View Comparison Table
            <FaArrowRight />
          </Link>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            How It <span className="text-gradient">Works</span>
          </h2>
          <p className="text-xl text-dark-muted max-w-2xl mx-auto">
            Find your perfect casino in three simple steps
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              step: '01',
              title: 'Browse Reviews',
              description: 'Explore detailed reviews of top UK casinos with honest ratings and insights',
              icon: FaStar,
            },
            {
              step: '02',
              title: 'Compare Offers',
              description: 'Use our comparison tool to find the best bonuses and features for you',
              icon: FaChartLine,
            },
            {
              step: '03',
              title: 'Claim & Play',
              description: 'Sign up through our links to claim exclusive bonuses and start playing',
              icon: FaGift,
            },
          ].map((item, index) => (
            <div
              key={index}
              className="glass-effect rounded-2xl p-8 text-center hover:scale-105 transition-transform"
            >
              <div className="text-6xl font-bold text-accent-primary/20 mb-4">{item.step}</div>
              <div className="bg-accent-primary/20 p-4 rounded-full inline-flex mb-4">
                <item.icon className="text-3xl text-accent-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
              <p className="text-dark-muted">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Bonuses Section */}
      <section id="bonuses" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Exclusive <span className="text-gradient">Bonuses</span>
            </h2>
            <p className="text-xl text-dark-muted max-w-2xl mx-auto">
              Unlock special offers available only through GameObsidian
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { type: 'Welcome Bonus', amount: 'Up to £500', icon: FaGift },
              { type: 'Free Spins', amount: '100+ Spins', icon: FaCoins },
              { type: 'Cashback', amount: '10% Weekly', icon: FaCreditCard },
              { type: 'VIP Rewards', amount: 'Exclusive', icon: FaCrown },
            ].map((bonus, index) => (
              <div
                key={index}
                className="glass-effect rounded-xl p-6 text-center hover:scale-105 transition-transform"
              >
                <div className="bg-accent-primary/20 p-4 rounded-full inline-flex mb-4">
                  <bonus.icon className="text-3xl text-accent-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{bonus.type}</h3>
                <p className="text-accent-primary font-bold text-xl">{bonus.amount}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-20 container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Secure <span className="text-gradient">Payment Methods</span>
          </h2>
          <p className="text-xl text-dark-muted max-w-2xl mx-auto">
            Fast, secure, and trusted payment options
          </p>
        </div>

        <div className="glass-effect rounded-2xl p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {['Visa', 'Mastercard', 'PayPal', 'Skrill', 'Neteller', 'Bank Transfer'].map(
              (method, index) => (
                <div
                  key={index}
                  className="bg-dark-card rounded-lg p-4 text-center hover:bg-accent-primary/20 transition-colors"
                >
                  <FaCreditCard className="text-3xl text-accent-primary mx-auto mb-2" />
                  <p className="font-semibold">{method}</p>
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* Game Providers Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Top <span className="text-gradient">Game Providers</span>
            </h2>
            <p className="text-xl text-dark-muted max-w-2xl mx-auto">
              Premium games from the industry's leading developers
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {[
              'NetEnt',
              'Microgaming',
              'Evolution',
              'Play\'n GO',
              'Pragmatic Play',
              'IGT',
            ].map((provider, index) => (
              <div
                key={index}
                className="glass-effect rounded-xl p-6 text-center hover:scale-105 transition-transform"
              >
                <FaGamepad className="text-4xl text-accent-primary mx-auto mb-3" />
                <p className="font-semibold">{provider}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Gaming Section */}
      <section className="py-20 container mx-auto px-4">
        <div className="glass-effect rounded-2xl p-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="bg-accent-primary/20 p-4 rounded-full inline-flex mb-6">
                <FaMobileAlt className="text-4xl text-accent-primary" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Play on the <span className="text-gradient">Go</span>
              </h2>
              <p className="text-xl text-dark-muted mb-6">
                Enjoy your favorite casino games anywhere, anytime. All top casinos offer 
                fully optimized mobile experiences for iOS and Android devices.
              </p>
              <ul className="space-y-3 mb-6">
                {['Responsive mobile websites', 'Native iOS & Android apps', 'Touch-optimized controls', 'Full game library access'].map(
                  (feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <FaCheck className="text-green-500 flex-shrink-0" />
                      <span className="text-dark-muted">{feature}</span>
                    </li>
                  )
                )}
              </ul>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-accent-primary/20 to-accent-secondary/20 rounded-2xl p-8 aspect-square flex items-center justify-center">
                <img
                  src="/images/mobilecasino.webp"
                  alt="mobile casino"
                  className="w-full h-full object-contain"
                  width="600"
                  height="600"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Casino Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Live <span className="text-gradient">Casino Experience</span>
            </h2>
            <p className="text-xl text-dark-muted max-w-2xl mx-auto">
              Immerse yourself in real-time casino action with professional dealers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Live Blackjack',
                description: 'Play against real dealers in HD quality streams',
                icon: FaPlay,
              },
              {
                title: 'Live Roulette',
                description: 'Experience the thrill of European and American roulette',
                icon: FaGamepad,
              },
              {
                title: 'Live Baccarat',
                description: 'Join VIP tables with high-stakes action',
                icon: FaCrown,
              },
            ].map((game, index) => (
              <div
                key={index}
                className="glass-effect rounded-xl p-8 text-center hover:scale-105 transition-transform"
              >
                <div className="bg-accent-primary/20 p-4 rounded-full inline-flex mb-4">
                  <game.icon className="text-3xl text-accent-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{game.title}</h3>
                <p className="text-dark-muted">{game.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIP Programs Section */}
      <section className="py-20 container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            VIP <span className="text-gradient">Rewards Programs</span>
          </h2>
          <p className="text-xl text-dark-muted max-w-2xl mx-auto">
            Unlock exclusive benefits as you play
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-6">
          {[
            { tier: 'Bronze', benefits: ['Welcome bonus', 'Email support'], color: 'from-amber-600 to-amber-800' },
            { tier: 'Silver', benefits: ['Higher bonuses', 'Priority support'], color: 'from-gray-400 to-gray-600' },
            { tier: 'Gold', benefits: ['Exclusive offers', 'Personal account manager'], color: 'from-accent-gold to-yellow-600' },
            { tier: 'Platinum', benefits: ['VIP events', 'Custom rewards'], color: 'from-accent-primary to-accent-secondary' },
          ].map((tier, index) => (
            <div
              key={index}
              className="glass-effect rounded-xl p-6 text-center hover:scale-105 transition-transform"
            >
              <div className={`bg-gradient-to-br ${tier.color} p-4 rounded-full inline-flex mb-4`}>
                <FaCrown className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">{tier.tier}</h3>
              <ul className="space-y-2 text-sm text-dark-muted">
                {tier.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-center justify-center gap-2">
                    <FaCheck className="text-green-500" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Security & Trust Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="glass-effect rounded-2xl p-12">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="bg-accent-primary/20 p-4 rounded-full inline-flex mb-6">
                  <FaLock className="text-4xl text-accent-primary" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Security & <span className="text-gradient">Trust</span>
                </h2>
                <p className="text-xl text-dark-muted mb-6">
                  Your safety is our priority. All recommended casinos are fully licensed 
                  and regulated by the UK Gambling Commission.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'UKGC Licensed', icon: FaCertificate },
                    { label: 'SSL Encrypted', icon: FaShieldAlt },
                    { label: 'Fair Gaming', icon: FaCheck },
                    { label: '24/7 Support', icon: FaClock },
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <item.icon className="text-accent-primary text-xl" />
                      <span className="font-semibold">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { name: 'UKGC', desc: 'Licensed' },
                  { name: 'SSL', desc: 'Secure' },
                  { name: 'RNG', desc: 'Fair' },
                  { name: 'GDPR', desc: 'Compliant' },
                ].map((badge, index) => (
                  <div
                    key={index}
                    className="bg-dark-card rounded-xl p-6 text-center hover:bg-accent-primary/20 transition-colors"
                  >
                    <FaCertificate className="text-4xl text-accent-primary mx-auto mb-2" />
                    <p className="font-bold text-lg">{badge.name}</p>
                    <p className="text-dark-muted text-sm">{badge.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="py-20 container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Latest <span className="text-gradient">News & Updates</span>
          </h2>
          <p className="text-xl text-dark-muted max-w-2xl mx-auto">
            Stay informed about the latest casino offers and industry news
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              title: 'New Casino Launches in UK Market',
              date: 'Dec 02, 2025',
              excerpt: 'Discover the latest casino to enter the UK market with exclusive launch bonuses.',
              fullText: `The UK online casino market continues to expand with exciting new entrants offering competitive welcome packages and innovative gaming experiences. This month, we've seen several new casinos launch with UK Gambling Commission licenses, bringing fresh perspectives to the industry.

These new platforms are competing aggressively with established operators by offering enhanced welcome bonuses, lower wagering requirements, and unique game selections. Many are focusing on mobile-first experiences, recognizing that the majority of UK players prefer gaming on their smartphones and tablets.

What sets these new casinos apart is their commitment to responsible gaming from day one, with built-in deposit limits, self-exclusion tools, and clear information about odds and game mechanics. They're also leveraging the latest technology to provide faster payouts and more secure payment processing.

For players, this means more choice and better value. However, it's important to remember that new doesn't always mean better. We thoroughly test each new casino before recommending it, checking licensing, game fairness, customer support quality, and overall user experience.

If you're considering trying a newly launched casino, look for UKGC licensing, read our detailed reviews, and always start with small deposits to test the platform before committing larger amounts.`,
            },
            {
              title: 'Best Bonuses of December 2025',
              date: 'Dec 02, 2025',
              excerpt: 'Our roundup of the most generous casino bonuses available this month.',
              fullText: `December 2025 has brought some exceptional casino bonuses to the UK market. With operators competing for new players after the holiday season, we're seeing some of the most generous offers in recent memory.

The standout offers this month include match bonuses up to £500, free spin packages exceeding 100 spins, and innovative cashback programs that reward regular play. What's particularly exciting is the trend toward lower wagering requirements, with some casinos offering 20x or even 15x wagering on bonus funds.

We've also noticed an increase in no-deposit bonuses, though these typically come with higher wagering requirements. Some casinos are offering £10-£20 free play just for signing up, which is a great way to test a platform risk-free.

VIP programs are becoming more accessible, with faster progression through tiers and more meaningful rewards at each level. Cashback offers are particularly strong this month, with some casinos offering up to 20% weekly cashback on losses.

However, it's crucial to read the terms and conditions carefully. Some bonuses that look generous on the surface come with game restrictions, maximum bet limits, or time constraints that can affect your ability to meet wagering requirements. Our reviews always highlight these important details.

Remember, the best bonus for you depends on your playing style. High rollers might prefer larger match bonuses, while casual players might benefit more from free spins or cashback offers.`,
            },
            {
              title: 'Mobile Gaming Trends 2025',
              date: 'Dec 02, 2025',
              excerpt: 'Explore the latest trends in mobile casino gaming and what to expect this year.',
              fullText: `Mobile casino gaming continues to dominate the UK market, with over 70% of players now preferring to play on their smartphones and tablets. As we move through 2025, we're seeing several exciting trends that are shaping the future of mobile gambling.

Touch-optimized interfaces are becoming standard, with larger buttons, swipe gestures, and intuitive navigation designed specifically for mobile devices. Game developers are creating mobile-first experiences rather than simply adapting desktop games, resulting in smoother gameplay and better graphics.

Live dealer games are now fully optimized for mobile, with HD streaming that adapts to your connection speed. Many casinos are offering mobile-exclusive bonuses and promotions, recognizing the importance of this platform.

One of the most significant trends is the rise of progressive web apps (PWAs). These offer app-like experiences without requiring downloads from app stores, providing instant access and push notifications while maintaining the flexibility of web browsers.

Security on mobile has also improved dramatically, with biometric authentication (fingerprint and face ID) becoming standard for account access and withdrawals. This makes mobile gaming more secure than ever before.

Looking ahead, we expect to see more integration with mobile payment methods like Apple Pay and Google Pay, making deposits even faster and more convenient. Augmented reality features are also on the horizon, potentially allowing players to experience casino games in entirely new ways.

For the best mobile gaming experience, look for casinos with dedicated mobile apps or well-optimized mobile websites. Check our reviews for mobile-specific ratings and recommendations.`,
            },
          ].map((article, index) => (
            <div
              key={index}
              className="glass-effect rounded-xl p-6 hover:scale-105 transition-transform cursor-pointer"
              onClick={() => openNewsModal(article)}
            >
              <div className="flex items-center gap-3 mb-4">
                <FaNewspaper className="text-accent-primary" />
                <span className="text-dark-muted text-sm">{article.date}</span>
              </div>
              <h3 className="text-xl font-bold mb-3">{article.title}</h3>
              <p className="text-dark-muted mb-4">{article.excerpt}</p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openNewsModal(article);
                }}
                className="text-accent-primary hover:text-accent-secondary transition-colors font-semibold flex items-center gap-2"
              >
                Read More
                <FaArrowRight />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Jackpot Winners Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Recent <span className="text-gradient">Jackpot Winners</span>
            </h2>
            <p className="text-xl text-dark-muted max-w-2xl mx-auto">
              Real players, real wins from our recommended casinos
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Sarah L.', amount: '£125,000', game: 'Mega Fortune' },
              { name: 'James K.', amount: '£89,500', game: 'Hall of Gods' },
              { name: 'Emma W.', amount: '£67,200', game: 'Mega Moolah' },
            ].map((winner, index) => (
              <div
                key={index}
                className="glass-effect rounded-xl p-8 text-center hover:scale-105 transition-transform"
              >
                <div className="bg-accent-gold/20 p-4 rounded-full inline-flex mb-4">
                  <FaTrophy className="text-4xl text-accent-gold" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-gradient">{winner.amount}</h3>
                <p className="text-dark-muted mb-1">Won by {winner.name}</p>
                <p className="text-sm text-dark-muted">Playing {winner.game}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Expert <span className="text-gradient">Reviews</span>
          </h2>
          <p className="text-xl text-dark-muted max-w-2xl mx-auto">
            In-depth analysis from our team of casino experts
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              casino: 'CasinoCasino',
              rating: 4.8,
              highlights: ['Excellent game selection', 'Fast payouts', 'Great mobile app'],
              url: 'https://www.casinocasino.com',
              link: '/offers/casinocasino',
            },
            {
              casino: 'MrPlay',
              rating: 4.7,
              highlights: ['Generous bonuses', '24/7 support', 'VIP program'],
              url: 'https://www.mrplay.com/',
              link: '/offers/mrplay',
            },
          ].map((review, index) => (
            <div key={index} className="glass-effect rounded-xl p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold">{review.casino}</h3>
                <div className="flex items-center gap-1">
                  <FaStar className="text-accent-gold" />
                  <span className="font-bold text-xl">{review.rating}</span>
                </div>
              </div>
              <ul className="space-y-2 mb-6">
                {review.highlights.map((highlight, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <FaCheck className="text-green-500 flex-shrink-0" />
                    <span className="text-dark-muted">{highlight}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col gap-3">
                <a
                  href={review.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary hover:opacity-90 rounded-lg font-semibold text-center transition-opacity flex items-center justify-center gap-2"
                >
                  Play Now
                  <FaArrowRight />
                </a>
                <Link
                  to={review.link}
                  className="inline-flex items-center justify-center gap-2 text-accent-primary hover:text-accent-secondary transition-colors font-semibold text-sm"
                >
                  Read Full Review
                  <FaArrowRight />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Responsible Gaming Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="glass-effect rounded-2xl p-12">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="bg-accent-primary/20 p-4 rounded-full inline-flex mb-6">
                  <FaHandHoldingHeart className="text-4xl text-accent-primary" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Responsible <span className="text-gradient">Gaming</span>
                </h2>
                <p className="text-xl text-dark-muted mb-6">
                  We promote safe and responsible gambling. If you feel that gambling 
                  is becoming a problem, help is available.
                </p>
                <div className="space-y-3">
                  {['Set deposit limits', 'Take regular breaks', 'Never chase losses', 'Seek help if needed'].map(
                    (tip, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <FaCheck className="text-green-500 flex-shrink-0" />
                        <span className="text-dark-muted">{tip}</span>
                      </div>
                    )
                  )}
                </div>
              </div>
              <div className="space-y-4">
                {[
                  { name: 'BeGambleAware', url: 'https://www.begambleaware.org' },
                  { name: 'GamCare', url: 'https://www.gamcare.org.uk' },
                  { name: 'Gambling Therapy', url: 'https://www.gamblingtherapy.org' },
                ].map((org, index) => (
                  <a
                    key={index}
                    href={org.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block glass-effect rounded-lg p-4 hover:bg-accent-primary/20 transition-colors"
                  >
                    <p className="font-semibold">{org.name}</p>
                    <p className="text-sm text-dark-muted">Get help and support</p>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 container mx-auto px-4">
        <div className="glass-effect rounded-2xl p-12 text-center">
          <div className="bg-accent-primary/20 p-4 rounded-full inline-flex mb-6">
            <FaEnvelope className="text-4xl text-accent-primary" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Stay <span className="text-gradient">Updated</span>
          </h2>
          <p className="text-xl text-dark-muted mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter for exclusive bonuses and the latest casino offers
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-6 py-3 bg-dark-card border border-dark-border rounded-lg focus:outline-none focus:border-accent-primary text-dark-text"
            />
            <button className="px-8 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary hover:opacity-90 rounded-lg font-semibold transition-opacity">
              Subscribe
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              What Players <span className="text-gradient">Say</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'James M.',
                rating: 5,
                text: 'Found the perfect casino thanks to the detailed reviews. The bonus was exactly as described!',
              },
              {
                name: 'Sarah K.',
                rating: 5,
                text: 'The comparison tool saved me hours of research. Highly recommend GameObsidian!',
              },
              {
                name: 'Michael T.',
                rating: 5,
                text: 'Transparent reviews and honest ratings. This is the only site I trust for casino offers.',
              },
            ].map((testimonial, index) => (
              <div
                key={index}
                className="glass-effect rounded-xl p-6 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="text-accent-gold" />
                  ))}
                </div>
                <p className="text-dark-muted mb-4 italic">"{testimonial.text}"</p>
                <p className="font-semibold">— {testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* News Modal */}
      <NewsModal
        isOpen={isNewsModalOpen}
        onClose={closeNewsModal}
        article={selectedNews}
      />
    </>
  );
};

export default Home;

